import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, HardHat, Plus, Search, Trash2, Users } from "lucide-react";
import { Link } from "react-router-dom";
import type { AccessGroupDto } from "@/services/api";
import { ROUTES } from "@/config/constants";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";
import { MultiSelect, type MultiSelectOption } from "@/components/ui/MultiSelect";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { Select } from "@/components/ui/Select";
import { cn } from "@/utils/cn";
import { type OperadorCatalogDto } from "@/services/locacoes";
import {
  isAdminUser,
  isOperadorAccount,
  resolveUserEffectiveAccess,
  userHasWarnings,
  userIsOperadorLinked,
  type UserRow,
} from "./accessUtils";

interface UsersTabProps {
  users: UserRow[];
  groups: AccessGroupDto[];
  groupOptions: MultiSelectOption[];
  allCcOptions: MultiSelectOption[];
  loading: boolean;
  initialGroupFilter?: string | null;
  savingUser: boolean;
  savingCcUserId: string | null;
  savingGroupUserId: string | null;
  savingPhoneUserId: string | null;
  savingDisplayNameUserId: string | null;
  savingPasswordUserId: string | null;
  savingRoleUserId: string | null;
  operadores: OperadorCatalogDto[];
  operadoresLoading: boolean;
  operadoresError?: string | null;
  onReloadOperadores?: () => void | Promise<void>;
  onCreateUser: (payload: {
    username: string;
    password: string;
    displayName: string;
    role: string;
    groupIds: string[];
    costCenterIds: string[];
    phone: string;
    isTi: boolean;
    operadorCatalogId?: string;
  }) => Promise<void>;
  onUpdateRole: (id: string, role: string, operadorCatalogId?: string | null) => Promise<void>;
  onUpdateGroups: (id: string, groupIds: string[]) => Promise<void>;
  onUpdateCostCenters: (id: string, costCenterIds: string[]) => Promise<void>;
  onUpdatePhone: (id: string, phone: string) => Promise<void>;
  onUpdateTi: (id: string, isTi: boolean) => Promise<void>;
  onUpdateDisplayName: (id: string, displayName: string) => Promise<void>;
  onUpdatePassword: (id: string, password: string) => Promise<void>;
  onDeleteUser: (id: string) => Promise<void>;
}

function UserStatusBadges({ user }: { user: UserRow }) {
  if (isAdminUser(user)) {
    return (
      <span className="flex flex-wrap gap-1">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-violet-700 bg-violet-50 px-1.5 py-0.5 rounded">
          Admin
        </span>
        {user.isTi && (
          <span className="text-[10px] font-semibold uppercase tracking-wide text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded">
            Equipe TI
          </span>
        )}
      </span>
    );
  }
  const badges: { label: string; className: string }[] = [];
  if (user.isTi) {
    badges.push({ label: "Equipe TI", className: "text-blue-700 bg-blue-50" });
  }
  if (isOperadorAccount(user)) {
    badges.push({ label: "Operador", className: "text-orange-800 bg-orange-50" });
  } else if (user.isOperadorOnly && user.locacoesOperador) {
    badges.push({ label: "Só operador", className: "text-orange-800 bg-orange-50" });
  } else if (user.locacoesOperador) {
    badges.push({ label: "Operador", className: "text-orange-800 bg-orange-50" });
  } else if (user.operadorCatalogId) {
    badges.push({ label: "Operador (inativo)", className: "text-slate-600 bg-slate-100" });
  }
  if ((user.groupIds ?? []).length === 0) {
    badges.push({ label: "Legado", className: "text-amber-800 bg-amber-50" });
  }
  if (user.costCenterIds === null) {
    badges.push({ label: "Todas obras", className: "text-amber-800 bg-amber-50" });
  } else if (user.costCenterIds.length === 0) {
    badges.push({ label: "Sem obras", className: "text-red-700 bg-red-50" });
  }
  if (badges.length === 0 && (user.groupIds ?? []).length > 0) {
    badges.push({
      label: `${user.groupIds.length} grupo${user.groupIds.length > 1 ? "s" : ""}`,
      className: "text-slate-600 bg-slate-100",
    });
  }
  return (
    <span className="flex flex-wrap gap-1">
      {badges.map((b) => (
        <span
          key={b.label}
          className={cn("text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded", b.className)}
        >
          {b.label}
        </span>
      ))}
    </span>
  );
}

function operadorSelectOptions(operadores: OperadorCatalogDto[], userId?: string) {
  return operadores
    .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"))
    .map((o) => {
      const extra = [o.documento, o.telefone].filter(Boolean).join(" · ");
      const linkedUid = o.usuarioId?.trim();
      const isOwn = Boolean(userId && linkedUid && linkedUid === userId);
      const isTaken = Boolean(linkedUid && !isOwn);
      const baseLabel = extra ? `${o.nome} (${extra})` : o.nome;
      return {
        value: o.id,
        label: isTaken
          ? `${baseLabel} — vinculado a ${o.linkedUserName?.trim() || "outro usuário"}`
          : baseLabel,
        disabled: isTaken,
      };
    });
}

function OperadorLinkField({
  operadores,
  value,
  userId,
  loading,
  loadError,
  onChange,
}: {
  operadores: OperadorCatalogDto[];
  value: string;
  userId?: string;
  loading?: boolean;
  loadError?: string | null;
  onChange: (operadorCatalogId: string) => void;
}) {
  const options = useMemo(() => operadorSelectOptions(operadores, userId), [operadores, userId]);
  const selectable = options.filter((o) => !o.disabled);
  const hasCatalog = operadores.length > 0;
  const hasSelectable = selectable.length > 0;

  let placeholder = "Selecione o operador cadastrado…";
  if (loading) placeholder = "Carregando operadores…";
  else if (!hasSelectable) placeholder = "Nenhum operador disponível";

  return (
    <div className="space-y-2">
      <SearchableSelect
        label="Operador (Locações)"
        value={value}
        options={hasSelectable ? selectable : options}
        onChange={onChange}
        placeholder={placeholder}
        loading={loading}
        disabled={loading || !hasSelectable}
      />
      {loadError && (
        <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          Não foi possível carregar operadores: {loadError}
        </p>
      )}
      {!loading && !loadError && !hasCatalog && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          Cadastre operadores em{" "}
          <Link to={ROUTES.LOCACOES} className="font-medium text-brand-700 hover:underline">
            Semog Locações → Cadastro → Operadores
          </Link>{" "}
          antes de criar o login.
        </p>
      )}
      {!loading && !loadError && hasCatalog && !hasSelectable && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          Todos os operadores cadastrados já possuem login vinculado. Cadastre um novo operador em{" "}
          <Link to={ROUTES.LOCACOES} className="font-medium text-brand-700 hover:underline">
            Semog Locações
          </Link>{" "}
          ou edite o usuário existente.
        </p>
      )}
      {hasSelectable && (
        <p className="text-xs text-slate-500">
          Após o login, o usuário acessa o app <code className="text-[11px]">/operador</code> nos contratos em que
          este operador estiver vinculado.
        </p>
      )}
    </div>
  );
}

function OperadorStatusCard({ user }: { user: UserRow }) {
  const active = user.locacoesOperador === true;
  const contratos = user.operadorContratosAtivos ?? 0;

  return (
    <div
      className={cn(
        "rounded-lg border px-3 py-3 space-y-1 text-sm",
        active ? "bg-orange-50 border-orange-200 text-orange-900" : "bg-slate-50 border-slate-200 text-slate-700",
      )}
    >
      {active ? (
        <>
          <p className="font-medium flex items-center gap-1.5">
            <HardHat className="w-4 h-4 shrink-0" />
            {contratos} contrato{contratos !== 1 ? "s" : ""} ativo{contratos !== 1 ? "s" : ""}
          </p>
          <p className="text-xs">
            Login redireciona para <code className="text-[11px] bg-white/80 px-1 rounded">/operador</code>.
          </p>
        </>
      ) : user.operadorCatalogId ? (
        <>
          <p className="font-medium flex items-center gap-1.5">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            Operador vinculado, sem contrato ativo
          </p>
          <p className="text-xs">
            Associe este operador a um contrato em{" "}
            <Link to={ROUTES.LOCACOES} className="text-brand-700 hover:underline font-medium">
              Semog Locações
            </Link>{" "}
            para liberar horímetro e manutenção.
          </p>
        </>
      ) : null}
    </div>
  );
}

function EffectiveAccessPreview({ user, groups }: { user: UserRow; groups: AccessGroupDto[] }) {
  const effective = resolveUserEffectiveAccess(user, groups);

  if (effective.kind === "admin") {
    return (
      <div className="rounded-lg bg-violet-50 border border-violet-100 px-3 py-2 text-sm text-violet-800">
        Acesso total — todos os módulos e todas as obras. Grupos e obras não se aplicam.
      </div>
    );
  }

  if (effective.kind === "legacy") {
    return (
      <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900">
        <p className="font-medium flex items-center gap-1.5">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          Acesso legado amplo
        </p>
        <p className="text-xs mt-1 text-amber-800">
          Sem grupos atribuídos, o usuário mantém permissões amplas do cadastro antigo. Atribua grupos para
          controlar os módulos.
        </p>
      </div>
    );
  }

  if (effective.kind === "empty") {
    return (
      <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-sm text-slate-600">
        Grupos inválidos ou removidos — nenhum módulo habilitado até reatribuir.
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-brand-50/60 border border-brand-100 px-3 py-2">
      <p className="text-xs font-semibold text-brand-800 mb-1.5">Acesso efetivo (união dos grupos)</p>
      <div className="flex flex-wrap gap-1">
        {user.locacoesOperador && (
          <span className="text-xs font-medium text-orange-900 bg-orange-50 border border-orange-200 px-2 py-0.5 rounded-md">
            App do Operador
          </span>
        )}
        {effective.labels.length > 0 ? (
          effective.labels.map((label) => (
            <span
              key={label}
              className="text-xs font-medium text-brand-900 bg-white border border-brand-100 px-2 py-0.5 rounded-md"
            >
              {label}
            </span>
          ))
        ) : !user.locacoesOperador ? (
          <p className="text-xs text-slate-500">Nenhum módulo habilitado pelos grupos selecionados.</p>
        ) : null}
      </div>
    </div>
  );
}

function UserDetailPanel({
  user,
  groups,
  groupOptions,
  allCcOptions,
  operadores,
  operadoresLoading,
  operadoresError,
  loading,
  savingCcUserId,
  savingGroupUserId,
  savingPhoneUserId,
  savingDisplayNameUserId,
  savingPasswordUserId,
  savingRoleUserId,
  onUpdateRole,
  onUpdateGroups,
  onUpdateCostCenters,
  onUpdatePhone,
  onUpdateTi,
  onUpdateDisplayName,
  onUpdatePassword,
  onDeleteUser,
}: {
  user: UserRow;
  groups: AccessGroupDto[];
  groupOptions: MultiSelectOption[];
  allCcOptions: MultiSelectOption[];
  operadores: OperadorCatalogDto[];
  operadoresLoading: boolean;
  operadoresError?: string | null;
  loading: boolean;
  savingCcUserId: string | null;
  savingGroupUserId: string | null;
  savingPhoneUserId: string | null;
  savingDisplayNameUserId: string | null;
  savingPasswordUserId: string | null;
  savingRoleUserId: string | null;
  onUpdateRole: (id: string, role: string, operadorCatalogId?: string | null) => Promise<void>;
  onUpdateGroups: (id: string, groupIds: string[]) => Promise<void>;
  onUpdateCostCenters: (id: string, costCenterIds: string[]) => Promise<void>;
  onUpdatePhone: (id: string, phone: string) => Promise<void>;
  onUpdateTi: (id: string, isTi: boolean) => Promise<void>;
  onUpdateDisplayName: (id: string, displayName: string) => Promise<void>;
  onUpdatePassword: (id: string, password: string) => Promise<void>;
  onDeleteUser: (id: string) => Promise<void>;
}) {
  const isAdmin = isAdminUser(user);
  const isOperador = isOperadorAccount(user);
  const [displayNameDraft, setDisplayNameDraft] = useState(user.displayName);
  const [phoneDraft, setPhoneDraft] = useState(user.phone ?? "");
  const [passwordDraft, setPasswordDraft] = useState("");
  const [passwordConfirmDraft, setPasswordConfirmDraft] = useState("");
  const [operadorDraft, setOperadorDraft] = useState(user.operadorCatalogId ?? "");
  const [switchingToOperador, setSwitchingToOperador] = useState(false);
  const displayNameDirty = displayNameDraft.trim() !== user.displayName;
  const phoneDirty = phoneDraft !== (user.phone ?? "");
  const passwordReady =
    passwordDraft.length >= 6 && passwordDraft === passwordConfirmDraft;
  const passwordMismatch =
    passwordConfirmDraft.length > 0 && passwordDraft !== passwordConfirmDraft;
  const operadorDirty = operadorDraft !== (user.operadorCatalogId ?? "");
  const showOperadorPanel = isOperador || switchingToOperador;

  useEffect(() => {
    setDisplayNameDraft(user.displayName);
    setPhoneDraft(user.phone ?? "");
    setPasswordDraft("");
    setPasswordConfirmDraft("");
    setOperadorDraft(user.operadorCatalogId ?? "");
    setSwitchingToOperador(false);
  }, [user.id, user.displayName, user.phone, user.operadorCatalogId, user.role]);

  const handleRoleChange = (role: string) => {
    if (role === "operador") {
      setSwitchingToOperador(true);
      setOperadorDraft(user.operadorCatalogId ?? "");
      return;
    }
    setSwitchingToOperador(false);
    void onUpdateRole(user.id, role, null);
  };

  const handleSavePassword = async () => {
    if (!passwordReady) return;
    try {
      await onUpdatePassword(user.id, passwordDraft);
      setPasswordDraft("");
      setPasswordConfirmDraft("");
    } catch {
      // Erro exibido pelo parent; mantém os campos para correção.
    }
  };

  const accountTypeValue = switchingToOperador ? "operador" : isOperador ? "operador" : user.role;

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-lg font-bold text-slate-800">{user.displayName}</h3>
        <p className="text-sm font-mono text-slate-500">@{user.username}</p>
      </div>

      <section className="space-y-2">
        <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Identidade</h4>
        <Input
          label="Nome exibido"
          value={displayNameDraft}
          onChange={(e) => setDisplayNameDraft(e.target.value)}
          hint="Nome mostrado no sistema e nas notificações."
        />
        {displayNameDirty && (
          <Button
            type="button"
            size="sm"
            onClick={() => void onUpdateDisplayName(user.id, displayNameDraft)}
            loading={savingDisplayNameUserId === user.id}
            disabled={!displayNameDraft.trim()}
          >
            Salvar nome
          </Button>
        )}
      </section>

      <section className="space-y-2">
        <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Senha</h4>
        <Input
          label="Nova senha"
          type="password"
          value={passwordDraft}
          onChange={(e) => setPasswordDraft(e.target.value)}
          autoComplete="new-password"
          hint="Mínimo de 6 caracteres."
        />
        <Input
          label="Confirmar senha"
          type="password"
          value={passwordConfirmDraft}
          onChange={(e) => setPasswordConfirmDraft(e.target.value)}
          autoComplete="new-password"
          error={passwordMismatch ? "As senhas não coincidem." : undefined}
        />
        <Button
          type="button"
          size="sm"
          onClick={() => void handleSavePassword()}
          loading={savingPasswordUserId === user.id}
          disabled={!passwordReady}
        >
          Alterar senha
        </Button>
      </section>

      <section className="space-y-2">
        <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Tipo de conta</h4>
        <select
          className="w-full max-w-xs border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white"
          value={accountTypeValue}
          onChange={(e) => handleRoleChange(e.target.value)}
          disabled={savingRoleUserId === user.id}
        >
          <option value="user">Usuário</option>
          <option value="operador">Operador</option>
          <option value="admin">Administrador</option>
        </select>
        {isAdmin && (
          <p className="text-xs text-slate-500">Administradores ignoram grupos e obras atribuídas.</p>
        )}
        {showOperadorPanel && (
          <p className="text-xs text-slate-500">
            Conta exclusiva para o app de campo em locações. Vincule ao cadastro do operador abaixo.
          </p>
        )}
      </section>

      <section className="space-y-2">
        <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Contato</h4>
        <Input
          label="Telefone WhatsApp"
          placeholder="+55 86 995887672"
          value={phoneDraft}
          onChange={(e) => setPhoneDraft(e.target.value)}
          hint="Usado para notificar aprovadores de solicitações de pagamento."
        />
        {phoneDirty && (
          <Button
            type="button"
            size="sm"
            onClick={() => void onUpdatePhone(user.id, phoneDraft)}
            loading={savingPhoneUserId === user.id}
          >
            Salvar telefone
          </Button>
        )}
        <label className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-700">
          <input
            type="checkbox"
            className="mt-0.5 rounded border-slate-300 text-brand-600"
            checked={user.isTi}
            onChange={(event) => void onUpdateTi(user.id, event.target.checked)}
          />
          <span>
            <strong className="block font-semibold text-slate-800">Integrante da equipe de TI</strong>
            Pode visualizar todos os chamados, alterar o Kanban e recebe alertas no WhatsApp.
          </span>
        </label>
      </section>

      {!isAdmin && showOperadorPanel && (
        <section className="space-y-2">
          <OperadorLinkField
            operadores={operadores}
            value={operadorDraft}
            userId={user.id}
            loading={operadoresLoading || savingRoleUserId === user.id}
            loadError={operadoresError}
            onChange={setOperadorDraft}
          />
          {(operadorDirty || switchingToOperador) && (
            <Button
              type="button"
              size="sm"
              onClick={() => {
                void onUpdateRole(user.id, "operador", operadorDraft || null).then(() =>
                  setSwitchingToOperador(false),
                );
              }}
              loading={savingRoleUserId === user.id}
              disabled={!operadorDraft}
            >
              {switchingToOperador ? "Confirmar conta operador" : "Salvar vínculo"}
            </Button>
          )}
          {isOperador && <OperadorStatusCard user={user} />}
        </section>
      )}

      {!isAdmin && !showOperadorPanel && (
        <>
          <section className="space-y-2">
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Módulos (via grupos)</h4>
            <MultiSelect
              label="Grupos de acesso"
              value={user.groupIds ?? []}
              onChange={(groupIds) => void onUpdateGroups(user.id, groupIds)}
              options={groupOptions}
              placeholder="Nenhum (acesso legado)"
              dropdownMinWidth={420}
              loading={savingGroupUserId === user.id}
            />
            {(user.groupIds ?? []).length === 0 && (
              <p className="text-xs text-amber-700 flex items-start gap-1">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                Sem grupo = acesso legado amplo aos módulos.
              </p>
            )}
            <EffectiveAccessPreview user={user} groups={groups} />
          </section>

          <section className="space-y-2">
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Obras visíveis</h4>
            {user.costCenterIds === null ? (
              <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-3 space-y-2">
                <p className="text-sm font-medium text-amber-900 flex items-center gap-1.5">
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                  Todas as obras (legado)
                </p>
                <p className="text-xs text-amber-800">
                  Este usuário enxerga todos os centros de custo. Restrinja para limitar o escopo de dados.
                </p>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() => void onUpdateCostCenters(user.id, [])}
                >
                  Restringir obras…
                </Button>
              </div>
            ) : (
              <>
                <MultiSelect
                  label="Centros de custo / obras"
                  value={user.costCenterIds ?? []}
                  onChange={(costCenterIds) => void onUpdateCostCenters(user.id, costCenterIds)}
                  options={allCcOptions}
                  placeholder="Nenhuma obra"
                  dropdownMinWidth={480}
                  loading={loading || savingCcUserId === user.id}
                />
                {(user.costCenterIds ?? []).length === 0 && (
                  <p className="text-xs text-red-700 flex items-start gap-1">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    Nenhuma obra atribuída — o usuário não verá dados de obras até configurar.
                  </p>
                )}
              </>
            )}
          </section>
        </>
      )}

      <div className="pt-2 border-t border-slate-100">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-red-600"
          onClick={() => void onDeleteUser(user.id)}
          leftIcon={<Trash2 className="w-3.5 h-3.5" />}
        >
          Excluir usuário
        </Button>
      </div>
    </div>
  );
}

export function UsersTab({
  users,
  groups,
  groupOptions,
  allCcOptions,
  loading,
  initialGroupFilter,
  savingUser,
  savingCcUserId,
  savingGroupUserId,
  savingPhoneUserId,
  savingDisplayNameUserId,
  savingPasswordUserId,
  savingRoleUserId,
  operadores,
  operadoresLoading,
  operadoresError,
  onReloadOperadores,
  onCreateUser,
  onUpdateRole,
  onUpdateGroups,
  onUpdateCostCenters,
  onUpdatePhone,
  onUpdateTi,
  onUpdateDisplayName,
  onUpdatePassword,
  onDeleteUser,
}: UsersTabProps) {
  const [search, setSearch] = useState("");
  const [groupFilter, setGroupFilter] = useState<string>("");
  const [warningsOnly, setWarningsOnly] = useState(false);
  const [operadoresOnly, setOperadoresOnly] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createStep, setCreateStep] = useState<1 | 2>(1);
  const [newUser, setNewUser] = useState({
    username: "",
    password: "",
    displayName: "",
    role: "user",
    groupIds: [] as string[],
    costCenterIds: [] as string[],
    phone: "",
    isTi: false,
    operadorCatalogId: "",
  });

  const isOperadorCreate = newUser.role === "operador";
  const createStepTitle =
    createStep === 1
      ? "Novo usuário — identidade"
      : isOperadorCreate
        ? "Novo usuário — operador"
        : "Novo usuário — acessos";

  useEffect(() => {
    if (createOpen && createStep === 2 && isOperadorCreate) {
      void onReloadOperadores?.();
    }
  }, [createOpen, createStep, isOperadorCreate, onReloadOperadores]);

  useEffect(() => {
    if (initialGroupFilter) {
      setGroupFilter(initialGroupFilter);
    }
  }, [initialGroupFilter]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (warningsOnly && !userHasWarnings(u)) return false;
      if (operadoresOnly && !userIsOperadorLinked(u)) return false;
      if (groupFilter && !(u.groupIds ?? []).includes(groupFilter)) return false;
      if (!q) return true;
      return (
        u.displayName.toLowerCase().includes(q) ||
        u.username.toLowerCase().includes(q)
      );
    });
  }, [users, search, groupFilter, warningsOnly, operadoresOnly]);

  useEffect(() => {
    if (filteredUsers.length === 0) {
      setSelectedId(null);
      return;
    }
    if (!selectedId || !filteredUsers.some((u) => u.id === selectedId)) {
      setSelectedId(filteredUsers[0].id);
    }
  }, [filteredUsers, selectedId]);

  const selectedUser = users.find((u) => u.id === selectedId) ?? null;

  const resetCreateForm = () => {
    setNewUser({
      username: "",
      password: "",
      displayName: "",
      role: "user",
      groupIds: [],
      costCenterIds: [],
      phone: "",
      isTi: false,
      operadorCatalogId: "",
    });
    setCreateStep(1);
  };

  const closeCreate = () => {
    if (savingUser) return;
    setCreateOpen(false);
    resetCreateForm();
  };

  const handleCreate = async () => {
    await onCreateUser(newUser);
    closeCreate();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-600">
          Atribua grupos (módulos) e obras (escopo de dados) a cada pessoa.
        </p>
        <Button
          type="button"
          size="sm"
          onClick={() => {
            resetCreateForm();
            setCreateOpen(true);
          }}
          leftIcon={<Plus className="w-4 h-4" />}
        >
          Novo usuário
        </Button>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 min-h-[calc(100vh-280px)]">
        <div className="lg:w-[360px] xl:w-[400px] shrink-0 flex flex-col rounded-xl border border-slate-200 bg-white overflow-hidden max-h-[calc(100vh-280px)]">
          <div className="p-3 border-b border-slate-100 space-y-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="search"
                placeholder="Buscar nome ou login…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500/30"
              />
            </div>
            <Select
              value={groupFilter}
              onChange={(e) => setGroupFilter(e.target.value)}
              options={groupOptions}
              placeholder="Todos os grupos"
            />
            <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
              <input
                type="checkbox"
                className="rounded border-slate-300 text-brand-600"
                checked={operadoresOnly}
                onChange={(e) => setOperadoresOnly(e.target.checked)}
              />
              Somente operadores (Locações)
            </label>
            <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
              <input
                type="checkbox"
                className="rounded border-slate-300 text-brand-600"
                checked={warningsOnly}
                onChange={(e) => setWarningsOnly(e.target.checked)}
              />
              Somente com avisos (legado / sem obras)
            </label>
          </div>

          <ul className="flex-1 overflow-y-auto divide-y divide-slate-100">
            {filteredUsers.map((u) => (
              <li key={u.id}>
                <button
                  type="button"
                  onClick={() => setSelectedId(u.id)}
                  className={cn(
                    "w-full text-left px-3 py-2.5 hover:bg-slate-50 transition-colors",
                    selectedId === u.id && "bg-brand-50 hover:bg-brand-50",
                  )}
                >
                  <p className="text-sm font-medium text-slate-800 truncate">{u.displayName}</p>
                  <p className="text-xs font-mono text-slate-400 truncate">@{u.username}</p>
                  <div className="mt-1">
                    <UserStatusBadges user={u} />
                  </div>
                </button>
              </li>
            ))}
            {filteredUsers.length === 0 && (
              <li className="px-3 py-8 text-sm text-slate-500 text-center">Nenhum usuário encontrado.</li>
            )}
          </ul>
        </div>

        <div className="flex-1 rounded-xl border border-slate-200 bg-white p-5 min-w-0 overflow-y-auto max-h-[calc(100vh-280px)]">
          {selectedUser ? (
            <UserDetailPanel
              user={selectedUser}
              groups={groups}
              groupOptions={groupOptions}
              allCcOptions={allCcOptions}
              operadores={operadores}
              operadoresLoading={operadoresLoading}
              operadoresError={operadoresError}
              loading={loading}
              savingCcUserId={savingCcUserId}
              savingGroupUserId={savingGroupUserId}
              savingPhoneUserId={savingPhoneUserId}
              savingDisplayNameUserId={savingDisplayNameUserId}
              savingPasswordUserId={savingPasswordUserId}
              savingRoleUserId={savingRoleUserId}
              onUpdateRole={onUpdateRole}
              onUpdateGroups={onUpdateGroups}
              onUpdateCostCenters={onUpdateCostCenters}
              onUpdatePhone={onUpdatePhone}
              onUpdateTi={onUpdateTi}
              onUpdateDisplayName={onUpdateDisplayName}
              onUpdatePassword={onUpdatePassword}
              onDeleteUser={onDeleteUser}
            />
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-slate-400 gap-2 py-16">
              <Users className="w-10 h-10" />
              <p className="text-sm">Selecione um usuário na lista</p>
            </div>
          )}
        </div>
      </div>

      <Modal
        open={createOpen}
        onClose={closeCreate}
        title={createStepTitle}
        size="md"
        footer={
          <div className="flex flex-wrap justify-between gap-2 w-full">
            <div>
              {createStep === 2 && newUser.role !== "admin" && (
                <Button type="button" variant="ghost" onClick={() => setCreateStep(1)} disabled={savingUser}>
                  Voltar
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="ghost" onClick={closeCreate} disabled={savingUser}>
                Cancelar
              </Button>
              {createStep === 1 ? (
                <Button
                  type="button"
                  onClick={() => {
                    if (newUser.role === "admin") void handleCreate();
                    else setCreateStep(2);
                  }}
                  disabled={!newUser.username.trim() || newUser.password.length < 6}
                >
                  {newUser.role === "admin" ? "Criar usuário" : "Próximo"}
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={() => void handleCreate()}
                  loading={savingUser}
                  disabled={
                    !newUser.username.trim() ||
                    newUser.password.length < 6 ||
                    (isOperadorCreate && !newUser.operadorCatalogId)
                  }
                  leftIcon={<Users className="w-4 h-4" />}
                >
                  Criar usuário
                </Button>
              )}
            </div>
          </div>
        }
      >
        {createStep === 1 ? (
          <div className="grid grid-cols-1 gap-3">
            <Input
              label="Login"
              value={newUser.username}
              onChange={(e) => setNewUser((s) => ({ ...s, username: e.target.value }))}
              autoComplete="off"
            />
            <Input
              label="Senha (mín. 6 caracteres)"
              type="password"
              value={newUser.password}
              onChange={(e) => setNewUser((s) => ({ ...s, password: e.target.value }))}
              autoComplete="new-password"
            />
            <Input
              label="Nome exibido"
              value={newUser.displayName}
              onChange={(e) => setNewUser((s) => ({ ...s, displayName: e.target.value }))}
            />
            <Input
              label="Telefone WhatsApp"
              placeholder="+55 86 995887672"
              value={newUser.phone}
              onChange={(e) => setNewUser((s) => ({ ...s, phone: e.target.value }))}
              hint="Opcional. Notificações de aprovação de pagamento."
            />
            <label className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-700">
              <input
                type="checkbox"
                className="mt-0.5 rounded border-slate-300 text-brand-600"
                checked={newUser.isTi}
                onChange={(event) =>
                  setNewUser((current) => ({ ...current, isTi: event.target.checked }))
                }
              />
              <span>
                <strong className="block font-semibold text-slate-800">Equipe de TI</strong>
                Permite gerenciar todos os chamados e receber alertas no WhatsApp.
              </span>
            </label>
            <Select
              label="Tipo de conta"
              value={newUser.role}
              onChange={(e) =>
                setNewUser((s) => ({
                  ...s,
                  role: e.target.value,
                  operadorCatalogId: e.target.value === "operador" ? s.operadorCatalogId : "",
                }))
              }
              options={[
                { value: "user", label: "Usuário" },
                { value: "operador", label: "Operador" },
                { value: "admin", label: "Administrador" },
              ]}
            />
          </div>
        ) : isOperadorCreate ? (
          <OperadorLinkField
            operadores={operadores}
            value={newUser.operadorCatalogId}
            loading={operadoresLoading}
            loadError={operadoresError}
            onChange={(operadorCatalogId) => setNewUser((s) => ({ ...s, operadorCatalogId }))}
          />
        ) : (
          <div className="space-y-3">
            <MultiSelect
              label="Grupos de acesso"
              value={newUser.groupIds}
              onChange={(groupIds) => setNewUser((s) => ({ ...s, groupIds }))}
              options={groupOptions}
              placeholder="Nenhum (acesso legado)"
              dropdownMinWidth={320}
            />
            <MultiSelect
              label="Obras / centros de custo"
              value={newUser.costCenterIds}
              onChange={(costCenterIds) => setNewUser((s) => ({ ...s, costCenterIds }))}
              options={allCcOptions}
              placeholder="Selecione as obras deste usuário"
              loading={loading}
              dropdownMinWidth={360}
            />
            <p className="text-xs text-slate-500">
              Vazio em obras = nenhuma obra até configurar. Sem grupos = acesso legado amplo aos módulos.
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
}
